import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  HallPatrolBreedingSnapshot,
  HallPatrolFeedingSnapshot,
  HallPatrolStockSnapshot,
  HallPatrolTankSnapshot,
  HallPatrolTaskSnapshot,
} from "./hallPatrol";

const PERMISSION_KEYS = [
  "feeding.record",
  "breeding.record",
  "water.record",
  "siphon.record",
  "treatment.record",
  "health.record",
  "tasks.record",
] as const;

interface PermissionDecisionRow {
  allowed?: boolean;
}

function firstRow<T>(value: unknown): T | undefined {
  if (Array.isArray(value)) return value[0] as T | undefined;
  if (value && typeof value === "object") return value as T;
  return undefined;
}

async function loadPermissions(client: SupabaseClient, farmId: string): Promise<string[]> {
  const decisions = await Promise.all(
    PERMISSION_KEYS.map(async (permission) => {
      const { data, error } = await client.rpc("permission_decide", {
        target_farm_id: farmId,
        requested_permission: permission,
      });
      if (error) throw error;
      return {
        permission,
        allowed: firstRow<PermissionDecisionRow>(data)?.allowed === true,
      };
    }),
  );

  return decisions.filter((decision) => decision.allowed).map((decision) => decision.permission);
}

export async function loadHallPatrolTankSnapshots(
  client: SupabaseClient,
  input: { farmId: string; userId: string; nowIso?: string },
): Promise<HallPatrolTankSnapshot[]> {
  const nowIso = input.nowIso ?? new Date().toISOString();

  const [permissions, tanksResult, occupancyResult, tasksResult, roundsResult] = await Promise.all([
    loadPermissions(client, input.farmId),
    client
      .from("tanks")
      .select("id,tank_code,is_active")
      .eq("farm_id", input.farmId)
      .order("tank_code"),
    client
      .from("tank_occupancies")
      .select("tank_id,occupant_kind,pair_id,stock_group_id")
      .eq("farm_id", input.farmId),
    client
      .from("hall_tasks")
      .select("id,tank_id,assigned_to,status,domain_version")
      .eq("farm_id", input.farmId)
      .neq("status", "completed"),
    client
      .from("feeding_round_runs")
      .select("id,scheduled_at,target_due_at,hard_cutoff_at,status")
      .eq("farm_id", input.farmId)
      .lte("scheduled_at", nowIso)
      .gte("hard_cutoff_at", nowIso)
      .order("scheduled_at", { ascending: false })
      .limit(2),
  ]);

  for (const result of [tanksResult, occupancyResult, tasksResult, roundsResult]) {
    if (result.error) throw result.error;
  }

  const occupancies = occupancyResult.data ?? [];
  const pairIds = [
    ...new Set(
      occupancies
        .map((row) => row.pair_id)
        .filter((value): value is string => typeof value === "string"),
    ),
  ];
  const stockIds = [
    ...new Set(
      occupancies
        .map((row) => row.stock_group_id)
        .filter((value): value is string => typeof value === "string"),
    ),
  ];

  const [pairsResult, cyclesResult, stocksResult] = await Promise.all([
    pairIds.length
      ? client
          .from("breeding_pairs")
          .select("id,domain_version,status")
          .eq("farm_id", input.farmId)
          .in("id", pairIds)
      : Promise.resolve({ data: [], error: null }),
    pairIds.length
      ? client
          .from("breeding_cycles")
          .select("id,pair_id,state,domain_version,spawned_at,separated_at")
          .eq("farm_id", input.farmId)
          .in("pair_id", pairIds)
          .not("state", "in", '("completed","failed")')
          .order("spawned_at", { ascending: false })
      : Promise.resolve({ data: [], error: null }),
    stockIds.length
      ? client
          .from("stock_groups")
          .select("id,domain_version,status")
          .eq("farm_id", input.farmId)
          .in("id", stockIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  for (const result of [pairsResult, cyclesResult, stocksResult]) {
    if (result.error) throw result.error;
  }

  const activeRoundIds = (roundsResult.data ?? []).map((row) => String(row.id));
  const roundTanksResult = activeRoundIds.length
    ? await client
        .from("feeding_round_tanks")
        .select("round_run_id,tank_id,status")
        .eq("farm_id", input.farmId)
        .in("round_run_id", activeRoundIds)
    : { data: [], error: null };

  if (roundTanksResult.error) throw roundTanksResult.error;

  type CycleRow = {
    id: unknown;
    pair_id: unknown;
    state: unknown;
    domain_version: unknown;
    spawned_at: unknown;
    separated_at: unknown;
  };

  const pairs = pairsResult.data ?? [];
  const stocks = stocksResult.data ?? [];
  const cycles = (cyclesResult.data ?? []) as CycleRow[];
  const pairById = new Map(pairs.map((row) => [String(row.id), row]));
  const stockById = new Map(stocks.map((row) => [String(row.id), row]));
  const latestCycleByPair = new Map<string, CycleRow>();
  for (const cycle of cycles) {
    const pairId = String(cycle.pair_id);
    if (!latestCycleByPair.has(pairId)) latestCycleByPair.set(pairId, cycle);
  }

  const occupancyByTank = new Map<string, typeof occupancies>();
  for (const occupancy of occupancies) {
    const tankId = String(occupancy.tank_id);
    const bucket = occupancyByTank.get(tankId) ?? [];
    bucket.push(occupancy);
    occupancyByTank.set(tankId, bucket);
  }

  const tasksByTank = new Map<string, HallPatrolTaskSnapshot[]>();
  for (const task of tasksResult.data ?? []) {
    if (!task.tank_id) continue;
    const tankId = String(task.tank_id);
    const bucket = tasksByTank.get(tankId) ?? [];
    bucket.push({
      taskId: String(task.id),
      taskVersion: Number(task.domain_version),
      assignedToCurrentActor: task.assigned_to === input.userId,
    });
    tasksByTank.set(tankId, bucket);
  }

  const roundById = new Map((roundsResult.data ?? []).map((row) => [String(row.id), row]));
  const feedingByTank = new Map<string, HallPatrolFeedingSnapshot>();
  for (const item of roundTanksResult.data ?? []) {
    const round = roundById.get(String(item.round_run_id));
    if (!round) continue;

    const status = String(item.status) as HallPatrolFeedingSnapshot["itemStatus"];
    const mode: HallPatrolFeedingSnapshot["mode"] =
      new Date(String(round.target_due_at)).getTime() < new Date(nowIso).getTime()
        ? "late"
        : "current";

    if (!feedingByTank.has(String(item.tank_id))) {
      feedingByTank.set(String(item.tank_id), {
        mode,
        roundRunId: String(item.round_run_id),
        itemStatus: status,
      });
    }
  }

  return (tanksResult.data ?? []).map((tank) => {
    const tankId = String(tank.id);
    const tankOccupancies = occupancyByTank.get(tankId) ?? [];
    const pairOccupancy = tankOccupancies.find(
      (row) => row.occupant_kind === "pair" && row.pair_id,
    );
    const stockOccupancy = tankOccupancies.find(
      (row) => row.occupant_kind === "stock_group" && row.stock_group_id,
    );

    let breeding: HallPatrolBreedingSnapshot | undefined;
    if (pairOccupancy?.pair_id) {
      const pair = pairById.get(String(pairOccupancy.pair_id));
      const cycle = latestCycleByPair.get(String(pairOccupancy.pair_id));
      if (pair) {
        breeding = {
          pairId: String(pair.id),
          pairVersion: Number(pair.domain_version),
          ...(cycle
            ? {
                cycleId: String(cycle.id),
                cycleVersion: Number(cycle.domain_version),
                cycleState: String(cycle.state) as NonNullable<
                  HallPatrolBreedingSnapshot["cycleState"]
                >,
                ...(typeof cycle.separated_at === "string"
                  ? { separatedAt: cycle.separated_at }
                  : {}),
              }
            : {}),
        };
      }
    }

    let stock: HallPatrolStockSnapshot | undefined;
    if (stockOccupancy?.stock_group_id) {
      const stockRow = stockById.get(String(stockOccupancy.stock_group_id));
      if (stockRow) {
        stock = {
          stockGroupId: String(stockRow.id),
          stockVersion: Number(stockRow.domain_version),
        };
      }
    }

    const feeding = feedingByTank.get(tankId);
    const pendingTasks = tasksByTank.get(tankId);

    return {
      farmId: input.farmId,
      tankId,
      tankCode: String(tank.tank_code),
      active: Boolean(tank.is_active),
      permissions,
      ...(breeding ? { breeding } : {}),
      ...(feeding ? { feeding } : {}),
      ...(stock ? { stock } : {}),
      ...(pendingTasks ? { pendingTasks } : {}),
    };
  });
}
